import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import InputAdornment from "@mui/material/InputAdornment";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";

// Services
import storageService from "services/storageService";
import walletService from "services/walletService";

// Icons
import { IoLockClosed, IoEye, IoEyeOff } from "react-icons/io5";

function WalletEncryption() {
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [autoLock, setAutoLock] = useState(true);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [lockTimer, setLockTimer] = useState("10");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if wallet is encrypted
    const hasWallet = storageService.hasUserAccount();
    setIsEncrypted(hasWallet);
    
    // Load email if exists
    const savedEmail = storageService.loadUserEmail();
    if (savedEmail) {
      setEmail(savedEmail);
    }
  }, []);

  const handleSetPassword = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    // Validation
    if (!email) {
      setError("Please enter an email address");
      setLoading(false);
      return;
    }

    if (!newPassword) {
      setError("Please enter a new password");
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      setLoading(false);
      return;
    }

    try {
      // Get current session private key
      const privateKey = storageService.loadSession();
      if (!privateKey) {
        setError("Please log in first");
        setLoading(false);
        return;
      }

      // Verify private key is valid
      const address = await walletService.getAddressFromWif(privateKey);
      if (!address) {
        setError("Invalid wallet session");
        setLoading(false);
        return;
      }

      // Save encrypted wallet
      const saved = storageService.saveWallet(privateKey, newPassword);
      if (!saved) {
        setError("Failed to encrypt wallet");
        setLoading(false);
        return;
      }

      // Save email
      storageService.saveUserEmail(email);

      setSuccess("✅ Wallet encrypted successfully! You can now log in with email & password.");
      setIsEncrypted(true);
      
      // Clear password fields
      setNewPassword("");
      setConfirmPassword("");
      
    } catch (err) {
      setError("Error encrypting wallet: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setError("");
    setSuccess("");
    setLoading(true);

    // Validation
    if (!currentPassword) {
      setError("Please enter your current password");
      setLoading(false);
      return;
    }

    if (!newPassword) {
      setError("Please enter a new password");
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("New passwords don't match");
      setLoading(false);
      return;
    }

    try {
      // Try to decrypt with current password
      const privateKey = storageService.loadWallet(currentPassword);
      if (!privateKey) {
        setError("Current password is incorrect");
        setLoading(false);
        return;
      }

      // Re-encrypt with new password
      const saved = storageService.saveWallet(privateKey, newPassword);
      if (!saved) {
        setError("Failed to save new password");
        setLoading(false);
        return;
      }

      setSuccess("✅ Password changed successfully!");
      
      // Clear all password fields
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
    } catch (err) {
      setError("Error changing password: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <VuiBox p={3}>
        <VuiBox mb={3}>
          <VuiBox display="flex" alignItems="center" mb={1}>
            <VuiBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="48px"
              height="48px"
              borderRadius="12px"
              sx={{ background: "#00CED1" }}
              mr={2}
            >
              <IoLockClosed size="24px" color="#000000" />
            </VuiBox>
            <VuiTypography variant="lg" color="white" fontWeight="bold">
              Wallet Encryption
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="button" color="text" ml={7}>
            Secure your wallet with password
          </VuiTypography>
        </VuiBox>

        {/* Encryption Status */}
        <VuiBox mb={3} p={2} sx={{ background: isEncrypted ? "rgba(0, 206, 209, 0.1)" : "rgba(255, 0, 128, 0.1)", borderRadius: "8px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center">
            <VuiBox>
              <VuiTypography variant="button" color="white" fontWeight="bold">
                Wallet Status
              </VuiTypography>
              <VuiTypography variant="caption" color="text" display="block">
                {isEncrypted ? "Your wallet is encrypted" : "Not encrypted - set password below"}
              </VuiTypography>
            </VuiBox>
            <Icon sx={{ color: isEncrypted ? "#00CED1" : "#FF0080", fontSize: "24px" }}>
              {isEncrypted ? "lock" : "lock_open"}
            </Icon>
          </VuiBox>
        </VuiBox>

        {/* Error Message */}
        {error && (
          <VuiBox mb={2} p={2} sx={{ background: "rgba(255, 0, 128, 0.1)", borderRadius: "8px", border: "1px solid #FF0080" }}>
            <VuiBox display="flex" alignItems="center">
              <Icon sx={{ color: "#FF0080", mr: 1 }}>error</Icon>
              <VuiTypography variant="caption" color="error">
                {error}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        )}

        {/* Success Message */}
        {success && (
          <VuiBox mb={2} p={2} sx={{ background: "rgba(0, 206, 209, 0.1)", borderRadius: "8px", border: "1px solid #00CED1" }}>
            <VuiBox display="flex" alignItems="center">
              <Icon sx={{ color: "#00CED1", mr: 1 }}>check_circle</Icon>
              <VuiTypography variant="caption" color="success">
                {success}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        )}

        {/* Set Password (if not encrypted) */}
        {!isEncrypted && (
          <>
            <VuiTypography variant="button" color="white" fontWeight="bold" mb={2} display="block">
              Set Password & Encrypt Wallet
            </VuiTypography>

            <VuiBox mb={2}>
              <VuiTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
                Email Address
              </VuiTypography>
              <VuiInput
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </VuiBox>

            <VuiBox mb={2}>
              <VuiTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
                New Password (min 8 characters)
              </VuiTypography>
              <VuiBox position="relative">
                <VuiInput
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ pr: 5 }}
                />
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  sx={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#fff",
                    "&:hover": { background: "rgba(255, 255, 255, 0.1)" }
                  }}
                >
                  {showNewPassword ? <IoEyeOff size="20px" /> : <IoEye size="20px" />}
                </IconButton>
              </VuiBox>
            </VuiBox>

            <VuiBox mb={3}>
              <VuiTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
                Confirm Password
              </VuiTypography>
              <VuiBox position="relative">
                <VuiInput
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ pr: 5 }}
                />
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  sx={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#fff",
                    "&:hover": { background: "rgba(255, 255, 255, 0.1)" }
                  }}
                >
                  {showConfirmPassword ? <IoEyeOff size="20px" /> : <IoEye size="20px" />}
                </IconButton>
              </VuiBox>
            </VuiBox>

            <VuiButton
              variant="contained"
              color="success"
              fullWidth
              disabled={loading}
              onClick={handleSetPassword}
              sx={{
                background: "linear-gradient(135deg, #00FF00 0%, #00AA00 100%)",
                color: "#000",
                mb: 3,
                "&:hover": {
                  background: "linear-gradient(135deg, #00FF00 0%, #00AA00 100%)",
                }
              }}
            >
              <Icon sx={{ mr: 1 }}>lock</Icon>
              {loading ? "ENCRYPTING..." : "ENCRYPT WALLET"}
            </VuiButton>
          </>
        )}

        {/* Change Password (if encrypted) */}
        {isEncrypted && (
          <>
            <VuiTypography variant="button" color="white" fontWeight="bold" mb={2} display="block">
              Change Password
            </VuiTypography>

            <VuiBox mb={2}>
              <VuiTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
                Current Password
              </VuiTypography>
              <VuiBox position="relative">
                <VuiInput
                  type={showCurrentPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  sx={{ pr: 5 }}
                />
                <IconButton
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  sx={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#fff",
                    "&:hover": { background: "rgba(255, 255, 255, 0.1)" }
                  }}
                >
                  {showCurrentPassword ? <IoEyeOff size="20px" /> : <IoEye size="20px" />}
                </IconButton>
              </VuiBox>
            </VuiBox>

            <VuiBox mb={2}>
              <VuiTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
                New Password
              </VuiTypography>
              <VuiBox position="relative">
                <VuiInput
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ pr: 5 }}
                />
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  sx={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#fff",
                    "&:hover": { background: "rgba(255, 255, 255, 0.1)" }
                  }}
                >
                  {showNewPassword ? <IoEyeOff size="20px" /> : <IoEye size="20px" />}
                </IconButton>
              </VuiBox>
            </VuiBox>

            <VuiBox mb={3}>
              <VuiTypography variant="caption" color="text" fontWeight="medium" mb={1} display="block">
                Confirm New Password
              </VuiTypography>
              <VuiBox position="relative">
                <VuiInput
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ pr: 5 }}
                />
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  sx={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#fff",
                    "&:hover": { background: "rgba(255, 255, 255, 0.1)" }
                  }}
                >
                  {showConfirmPassword ? <IoEyeOff size="20px" /> : <IoEye size="20px" />}
                </IconButton>
              </VuiBox>
            </VuiBox>

            <VuiButton
              variant="contained"
              color="info"
              fullWidth
              disabled={loading}
              onClick={handleChangePassword}
              sx={{
                background: "linear-gradient(135deg, #0075F6 0%, #4B177E 100%)",
                mb: 3
              }}
            >
              <Icon sx={{ mr: 1 }}>vpn_key</Icon>
              {loading ? "CHANGING..." : "CHANGE PASSWORD"}
            </VuiButton>
          </>
        )}

        {/* Auto-Lock Settings */}
        <VuiBox p={2} sx={{ background: "rgba(255, 255, 255, 0.02)", borderRadius: "8px" }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <VuiTypography variant="button" color="white" fontWeight="medium">
              Auto-Lock Wallet
            </VuiTypography>
            <Switch checked={autoLock} onChange={(e) => setAutoLock(e.target.checked)} />
          </VuiBox>
          {autoLock && (
            <VuiBox>
              <VuiTypography variant="caption" color="text" mb={1} display="block">
                Lock after (minutes)
              </VuiTypography>
              <VuiInput
                type="number"
                value={lockTimer}
                onChange={(e) => setLockTimer(e.target.value)}
                sx={{ width: "100px" }}
              />
            </VuiBox>
          )}
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default WalletEncryption;
